import type { PublicPick } from "@/lib/publicPicks.functions";

const SITE = "https://statlign.com";
const slug = (v: string) => (v || "").toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const fixturePath = (p: Pick<PublicPick, "team_home" | "team_away" | "kickoff_time">) =>
  `/fixtures/${slug(p.team_home)}-vs-${slug(p.team_away)}-${p.kickoff_time.slice(0, 10)}`;

export function sportsEventLd(p: PublicPick) {
  const name = `${p.team_home} vs ${p.team_away}`;
  return {
    "@type": "SportsEvent",
    name,
    sport: "Soccer",
    startDate: p.kickoff_time,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url: `${SITE}${fixturePath(p)}`,
    location: { "@type": "Place", name: `${p.team_home} home ground` },
    homeTeam: { "@type": "SportsTeam", name: p.team_home },
    awayTeam: { "@type": "SportsTeam", name: p.team_away },
    competitor: [{ "@type": "SportsTeam", name: p.team_home }, { "@type": "SportsTeam", name: p.team_away }],
    organizer: { "@type": "Organization", name: p.league ?? "Football", url: SITE },
    description: p.reason ?? `${name} prediction and analysis by Statlign.`,
  };
}

/** Server-rendered JSON-LD scripts for a hub page: CollectionPage + ItemList of fixtures + breadcrumbs. */
export function hubJsonLd(opts: { path: string; name: string; description: string; picks: PublicPick[]; crumbs?: { name: string; path: string }[] }) {
  const url = `${SITE}${opts.path}`;
  const items = opts.picks.slice(0, 20);
  const graph: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: opts.name,
      description: opts.description,
      url,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: "Statlign", url: SITE },
      ...(items.length
        ? {
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: items.length,
              itemListElement: items.map((p, i) => ({ "@type": "ListItem", position: i + 1, item: sportsEventLd(p) })),
            },
          }
        : {}),
    },
  ];
  const crumbs = opts.crumbs ?? [{ name: "Home", path: "/" }, { name: opts.name, path: opts.path }];
  if (opts.path !== "/")
    graph.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `${SITE}${c.path}` })),
    });
  return graph.map((g) => ({ type: "application/ld+json", children: JSON.stringify(g) }));
}
